import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import axios from 'axios';
import SEO from '../components/SEO';

import { API_URL } from '../config/api';
const API = `${API_URL}/api`;

// Sample blog posts for initial display
const samplePosts = [
  {
    id: '1',
    title: 'Comment organiser son entreprise quand on est artisan',
    slug: 'comment-organiser-entreprise-artisan',
    excerpt: 'Découvre les meilleures pratiques pour structurer ton activité d\'artisan et gagner en efficacité au quotidien.',
    image_url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    author: 'Romain Trallero',
    category: 'Organisation',
    created_at: new Date().toISOString(),
  },
  {
    id: '2',
    title: 'Comment déléguer quand on est entrepreneur',
    slug: 'comment-deleguer-entrepreneur',
    excerpt: 'La délégation est essentielle pour développer ton entreprise. Apprenez à lâcher prise et faire confiance.',
    image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
    author: 'Romain Trallero',
    category: 'Délégation',
    created_at: new Date().toISOString(),
  },
  {
    id: '3',
    title: 'Pourquoi les entrepreneurs sont débordés',
    slug: 'pourquoi-entrepreneurs-debordes',
    excerpt: 'Analyse des causes principales de la surcharge de travail chez les entrepreneurs et solutions concrètes.',
    image_url: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800',
    author: 'Romain Trallero',
    category: 'Productivité',
    created_at: new Date().toISOString(),
  },
  {
    id: '4',
    title: 'Comment trouver des clients quand on est artisan',
    slug: 'trouver-clients-artisan',
    excerpt: 'Stratégies efficaces pour développer ta clientèle et sécuriser ton chiffre d\'affaires.',
    image_url: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800',
    author: 'Romain Trallero',
    category: 'Commercial',
    created_at: new Date().toISOString(),
  },
  {
    id: '5',
    title: 'Comment automatiser son entreprise',
    slug: 'automatiser-entreprise',
    excerpt: 'Les outils et méthodes pour automatiser les tâches répétitives et gagner du temps précieux.',
    image_url: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800',
    author: 'Romain Trallero',
    category: 'Automatisation',
    created_at: new Date().toISOString(),
  },
];

export default function BlogPage() {
  const [posts, setPosts] = useState(samplePosts);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        if (response.data.length > 0) {
          setPosts(response.data);
        }
      } catch (error) {
        console.log('Using sample posts');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <SEO page="blog" />
      <BlogContent 
        posts={filteredPosts} 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery} 
        loading={loading} 
      />
    </>
  );
}

function BlogContent({ posts, searchQuery, setSearchQuery, loading }) {

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="py-20 gradient-mesh">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-rm-text mb-6">
              Le Blog
            </h1>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8">
              Conseils et ressources pour organiser ton entreprise, 
              automatiser tes tâches et développer ton activité.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 rounded-full border-slate-200"
                data-testid="blog-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-rm-text-secondary text-lg">
                Aucun article trouvé pour "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl transition-all group blog-card"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="blog-card-image aspect-video">
                      <img 
                        src={post.image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6">
                      <span className="inline-block px-3 py-1 bg-rm-accent/20 text-rm-primary text-xs font-medium rounded-full mb-3">
                        {post.category}
                      </span>
                      <h2 className="text-xl font-bold text-rm-text mb-3 group-hover:text-rm-primary transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-rm-text-secondary text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-rm-text-secondary">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
